import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { SurveyService } from "../api/SurveyService";
import { WorkplaceCultureService } from "../api/WorkplaceCultureService";
import { ReportContainer } from "../components/ReportContainer/ReportContainer";
import { Typography } from "../components/ui/Typography/Typography";
import { useAuth } from "../hooks/useAuth";
import useTranslation from "../hooks/useTranslation";
import SurveyDispatch from "../models/surveyDispatch";
import {
  GetSurveyDispatchResponse,
  GetSurveyDispatchesResponse,
} from "../types/survey";
import {
  GetDemographicGroupsResponse,
  GetQuestionBreakdownResponse,
  GetSurveyChapterScoringResponse,
} from "../types/workplaceCulture";
import {
  ENGAGEMENT_THRESHOLD,
  getOrganizationDemographic,
} from "../utils/WorkplaceCulture.utils";
import "./ReportPage.scss";

export const ReportPage = () => {
  const { authInfo } = useAuth();
  const { t } = useTranslation("reportPage");
  const [surveyDispatch, setSurveyDispatch] = useState<SurveyDispatch | null>(
    null
  );

  const { dispatchId } = useParams<{ dispatchId: string }>();

  const surveyService = new SurveyService();
  const workplaceCultureService = new WorkplaceCultureService();

  // Add class to body for print-specific styling
  useEffect(() => {
    document.body.classList.add("report-page-active");
    return () => {
      document.body.classList.remove("report-page-active");
    };
  }, []);

  const {
    data: dispatchData,
    isLoading: dispatchLoading,
    error: dispatchError,
  } = useQuery<GetSurveyDispatchResponse, AxiosError>(
    ["latestDispatch"],
    () => {
      if (!dispatchId) throw new Error("Dispatch ID is required");
      return surveyService.getDispatch(dispatchId);
    },
    {
      enabled: !!dispatchId,
    }
  );

  const {
    data: surveyChapterScoringData,
    isLoading: surveyChapterScoringLoading,
    error: surveyChapterScoringError,
  } = useQuery<GetSurveyChapterScoringResponse, AxiosError>(
    [`surveyChapterScoring-${dispatchId}`],
    () => workplaceCultureService.getSurveyChapterScoring(dispatchId || ""),
    {
      enabled: !!dispatchId,
    }
  );

  const {
    data: surveyQuestionsData,
    isLoading: surveyQuestionsLoading,
    error: surveyQuestionsError,
  } = useQuery<GetQuestionBreakdownResponse, AxiosError>(
    [`surveyQuestions-${dispatchId}`],
    () => workplaceCultureService.getSurveyQuestionsScoring(dispatchId || ""),
    {
      enabled: !!dispatchId,
    }
  );

  const {
    data: surveyDemographicsData,
    isLoading: surveyDemographicsLoading,
    error: surveyDemographicsError,
  } = useQuery<GetDemographicGroupsResponse, AxiosError>(
    [`surveyDemographicGroups-${dispatchId}`],
    () => workplaceCultureService.getDemographicGroups(dispatchId || ""),
    {
      enabled: !!dispatchId,
    }
  );

  useEffect(() => {
    if (dispatchData) {
      const surveyDispatchModel = new SurveyDispatch(dispatchData);
      setSurveyDispatch(surveyDispatchModel);
    }
  }, [dispatchData]);

  const isLoading =
    dispatchLoading ||
    surveyChapterScoringLoading ||
    surveyQuestionsLoading ||
    surveyDemographicsLoading;

  const hasError =
    !!dispatchError ||
    !!surveyChapterScoringError ||
    !!surveyQuestionsError ||
    !!surveyDemographicsError;

  if (isLoading) {
    return (
      <div className="ReportPage">
        <Typography desktop="h1">{t("errors.loading")}</Typography>
      </div>
    );
  }

  // Handle 404 for invalid survey dispatch
  if (
    (dispatchError && dispatchError.response?.status === 404) ||
    dispatchError?.response?.status === 400
  ) {
    return <Navigate to="/404" replace />;
  }

  // Handle other errors or missing survey dispatch
  if (hasError || !surveyDispatch) {
    return (
      <div className="ReportPage">
        <Typography desktop="h1">{t("errors.loadingError")}</Typography>
      </div>
    );
  }

  // Handle survey that's not ready for report generation
  if (
    surveyDispatch.isActive() ||
    surveyDispatch.isReady() ||
    surveyDispatch.isInProgress()
  ) {
    const getStatusKey = () => {
      if (surveyDispatch.isActive()) return "statusActive";
      return "statusInProgress";
    };

    return (
      <div className="ReportPage">
        <Typography desktop="h1">{t("errors.surveyNotReady")}</Typography>
        <Typography desktop="body1">
          {t("errors.surveyNotReadyDescription", {
            status: t(`errors.${getStatusKey()}`),
          })}
        </Typography>
      </div>
    );
  }

  const orgDemographic = getOrganizationDemographic(surveyDemographicsData);
  if (!orgDemographic?.score) {
    return (
      <div className="ReportPage">
        <Typography desktop="h1">
          {t("errors.insufficientEngagement")}
        </Typography>
        <Typography desktop="body1">
          {t("errors.insufficientEngagementDescription", {
            current: surveyDispatch.engagement,
            minimum: ENGAGEMENT_THRESHOLD,
          })}
        </Typography>
      </div>
    );
  }

  return (
    <div className="ReportPage">
      <ReportContainer
        surveyDispatch={surveyDispatch}
        chapterScoringData={surveyChapterScoringData}
        questionsData={surveyQuestionsData}
        demographicsData={surveyDemographicsData}
        companyName={authInfo.user?.getCompanyName() || ""}
      />
    </div>
  );
};
